import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfidenceDotProps {
  confidence: 'confirmed' | 'likely';
  /** Hide the word visually (it stays for screen readers); never colour alone otherwise. */
  compact?: boolean;
}

/**
 * Confirmed vs likely attribution (brief 1.6): a filled 8px dot when the
 * platform said so, a hollow ring when timing alone suggests it. Always with
 * the word "Confirmed" or "Likely".
 */
export function ConfidenceDot({ confidence, compact = false }: ConfidenceDotProps): ReactElement {
  const { t } = useTranslation();
  const confirmed = confidence === 'confirmed';
  const word = confirmed ? t('confidence.confirmed') : t('confidence.likely');
  return (
    <span className="inline-flex items-center gap-1 text-meta text-[var(--ink-2)]" data-confidence={confidence}>
      <span
        className="inline-block w-2 h-2 rounded-full shrink-0"
        style={confirmed ? { background: 'var(--ink-2)' } : { border: '1.5px solid var(--ink-2)', background: 'transparent' }}
        aria-hidden="true"
      />
      <span className={compact ? 'sr-only' : ''}>{word}</span>
    </span>
  );
}
